import { useState, type ReactNode } from 'react'

import { SimpleTable, TableSkeleton, type AddressCell, Alert, AssetDetailModal } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

interface ReeferFuelParams extends InsightParametersType {
  companyId?: number
  assetIds?: string[]
  date?: string
  from?: string
  to?: string
}

interface ReeferFuel {
  id: number
  asset: {
    id: number
    name: string
  }
  deviceName: string
  sampleTime: Date
  fuelPercent: number | null
  latitude?: number
  longitude?: number
  location?: string
}

const ReeferFuel: InsightDefinition<ReeferFuelParams, ReeferFuel[]> = {
  active: true,
  slug: 'fuel/reefer-fuel',
  category: 'fuel',
  name: 'Reefer Fuel by Asset',
  description: 'Display reefer fuel level history for an asset for a given date range',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }: { parameters: ReeferFuelParams; field: keyof ReeferFuelParams }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetIds',
        control: 'AssetDropdownMulti',
        required: true,
        includeSelectAll: true,
        assetType: AssetTypeFilter.InTransit,
      },

      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
    ],

    isValid: (configuration: ReeferFuelParams) => {
      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Start and end date are required.'
        }
        const fromDate = new Date(configuration.from)
        const toDate = new Date(configuration.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }
        const dateValidation = validateDateRangeWithin30Days(fromDate.toISOString(), toDate.toISOString())
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      const hasAssets = configuration.assetIds && configuration.assetIds.length > 0

      if (configuration?.companyId && hasAssets) {
        return true
      }
      return 'Company and Assets are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferFuelParams) => {
    const query = buildQuery({ ...parameters, date: parameters.date || 'today' })
    const path = `/insights/fuel/reefer-fuel-by-asset?${query}&skip=0&take=1000`
    const response = await api.get<ReeferFuel[]>({
      path,
      origin: 'ReeferFuel',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferFuelParams, title: string) => {
    const path = '/insights/fuel/reefer-fuel-by-asset'
    // Keep the export window consistent with the table's default (see load).
    await downloadCsv(api, path, { ...parameters, date: parameters.date || 'today' }, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={5} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as ReeferFuelParams
    const headers = ['Asset', 'Device Name', 'Date', 'Fuel %', 'Location']
    const body = data
      .sort((a, b) => new Date(b.sampleTime).getTime() - new Date(a.sampleTime).getTime())
      .map(
        (sample): Record<string, ReactNode | AddressCell> => ({
          Asset: (
            <button
              onClick={() => setSelectedAsset(sample.asset.id)}
              className="text-left block text-blue-500 cursor-pointer"
            >
              {sample.asset.name ?? '-'}
            </button>
          ),
          'Device Name': sample.deviceName ?? '-',
          Date: formatDate(sample.sampleTime, standardDateTimeFormat),
          'Fuel %': sample.fuelPercent ?? '-',
          Location: genericLocationDrilldown({
            configuration,
            address: sample.location ?? undefined,
            latitude: sample.latitude,
            longitude: sample.longitude,
            companyId: parameters.companyId,
            markerFields: {
              Asset: sample.asset.name,
              Time: formatDate(sample.sampleTime, standardDateTimeFormat),
            },
          }),
        })
      )

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const params = configuration.parameters as ReeferFuelParams

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.assetIds?.length) {
      fields['Assets'] = `${params.assetIds.length} Asset${params.assetIds.length === 1 ? '' : 's'}`
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferFuel
